import React from "react";
import { create } from "zustand";
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

import metadata from "./data/site.config.json";
import { LocalDB } from "./utils/localDB";
import { t } from "./utils/translations";

const token_key = `${metadata.localDB.name}_token`;
const refresh_token_key = `${metadata.localDB.name}_refresh_token`;
const role_key = `${metadata.localDB.name}_role`;
const admin_flag_key = `${metadata.localDB.name}_is_admin`;
const church_id_key = `${metadata.localDB.name}_church_id`;
const username_key = `${metadata.localDB.name}_username`;
const authorized_pages_key = `${metadata.localDB.name}_authorized_pages`;
const table_access_key = `${metadata.localDB.name}_table_access`;
const data_grid_states_key = `${metadata.localDB.name}_data_grid_states`;

// ---- Axios interceptor for automatic token refresh on 401 ----
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) prom.resolve(token);
    else prom.reject(error);
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Only intercept 401s for authenticated API calls (not the signin endpoint itself)
    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.url === metadata.endpoints.signin
    ) {
      return Promise.reject(error);
    }

    const storedRefreshToken = window.localStorage.getItem(refresh_token_key);
    const storedUsername = window.localStorage.getItem(username_key);

    if (!storedRefreshToken || !storedUsername) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Another refresh is already in progress — queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axios(originalRequest));
          },
          reject: (err: any) => reject(err),
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshResponse = await axios.post(metadata.endpoints.signin, {
        username: storedUsername,
        refresh_token: storedRefreshToken,
      });

      const newAccessToken = refreshResponse.data.access_token;
      if (!newAccessToken) {
        throw new Error("No access_token in refresh response");
      }

      // Update stored token
      window.localStorage.setItem(token_key, newAccessToken);
      // Refresh flow doesn't return a new refresh_token — keep the existing one

      processQueue(null, newAccessToken);

      // Retry the original request with the new token
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Refresh failed — clear tokens and let the 401 error propagate
      // The errorModal handler will redirect to SignIn
      window.localStorage.removeItem(token_key);
      window.localStorage.removeItem(refresh_token_key);
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  },
);

interface ApiState {
  loading: boolean;
  modal: any;
  requisition: any;
  requisitions: [];
  clearLocalStorage: () => void;
  signin: (
    username: string,
    password: string,
  ) => Promise<{
    authenticated: boolean;
    isRoleAdmin?: boolean;
    requirePasswordChange?: boolean;
    session?: string;
  }>;
  respondToAuthChallenge: (
    username: string,
    newPassword: string,
    session: string,
  ) => Promise<{
    authenticated: boolean;
    isRoleAdmin?: boolean;
    requirePasswordChange?: boolean;
    session?: string;
  }>;
  register: (
    email: string,
    username: string,
    password: string,
    phoneNumber: string,
    firstName: string,
    lastName: string,
    ministryJustification: string,
    mailingAddress: string,
  ) => Promise<boolean>;
  changePassword: (
    username: string,
    oldPassword: string,
    newPassword: string,
  ) => Promise<boolean>;
  forgotPassword: (username: string, email: string) => Promise<boolean>;
  confirmForgotPassword: (
    username: string,
    confirmationCode: string,
    newPassword: string,
  ) => Promise<boolean>;
  showModal: (
    title: string,
    content: string | React.ReactNode,
    closeText?: string,
    actionButton?: React.ReactNode,
    closeAction?: string,
  ) => void;
  closeModal: () => void;
  updateOptionsLocally: (localDB: LocalDB) => void;
  getOptionsLocally: (localDB: LocalDB, key: string) => any;
  getAllData: (pageId: string) => Promise<any>;
  getDataById: (
    pageId: string,
    id: string,
    primaryKey?: string,
  ) => Promise<any>;
  saveData: (
    pageId: string,
    data: any,
    files: any,
    id?: string,
  ) => Promise<boolean>;
}

export const useApiStore = create<ApiState>((set) => ({
  loading: false,
  modal: { isOpen: false, title: undefined, content: undefined },
  requisition: {},
  requisitions: [],
  clearLocalStorage: () => {
    window.localStorage.removeItem(role_key);
    window.localStorage.removeItem(admin_flag_key);
    window.localStorage.removeItem(token_key);
    window.localStorage.removeItem(refresh_token_key);
    window.localStorage.removeItem(church_id_key);
    window.localStorage.removeItem(username_key);
    window.localStorage.removeItem(authorized_pages_key);
    window.localStorage.removeItem(table_access_key);
    window.localStorage.removeItem(data_grid_states_key);
  },
  signin: async (username: string, password: string) => {
    set({ loading: true });
    try {
      const response = await axios({
        method: "post",
        url: metadata.endpoints.signin,
        data: {
          username,
          password,
        },
      });

      if (
        response.data.status === "challenge" &&
        response.data.challenge === "NEW_PASSWORD_REQUIRED"
      ) {
        set({ loading: false });
        return {
          authenticated: false,
          requirePasswordChange: true,
          session: response.data.session,
        };
      }

      const pages = Object.entries(metadata.pages);
      const authorizedPages = response.data.table_access
        .map(
          (formTableName: string) =>
            pages.find(([_, page]) => page.table_name === formTableName)?.[0],
        )
        .filter((pageId: string | undefined) => pageId !== undefined);

      window.localStorage.setItem(role_key, response.data.role);
      window.localStorage.setItem(
        admin_flag_key,
        JSON.stringify(Boolean(response.data.is_admin)),
      );
      window.localStorage.setItem(token_key, response.data.access_token);
      if (response.data.refresh_token) {
        window.localStorage.setItem(
          refresh_token_key,
          response.data.refresh_token,
        );
      }
      window.localStorage.setItem(church_id_key, response.data.church_id);
      window.localStorage.setItem(username_key, username);
      window.localStorage.setItem(
        authorized_pages_key,
        JSON.stringify(authorizedPages),
      );
      // Store raw table_access for permission checking during data fetching
      window.localStorage.setItem(
        table_access_key,
        JSON.stringify(response.data.table_access || []),
      );
      set({ loading: false });
      return {
        authenticated: true,
        isRoleAdmin: Boolean(response.data.is_admin),
      };
    } catch (error: any) {
      set({
        loading: false,
        modal: errorModal(error),
      });
    }

    return { authenticated: false };
  },
  respondToAuthChallenge: async (
    username: string,
    newPassword: string,
    session: string,
  ) => {
    set({ loading: true });
    try {
      const response = await axios({
        method: "post",
        url: metadata.endpoints.signin,
        data: {
          username,
          new_password: newPassword,
          session,
        },
      });

      if (response.data.status === "challenge") {
        set({ loading: false });
        return {
          authenticated: false,
          requirePasswordChange:
            response.data.challenge === "NEW_PASSWORD_REQUIRED",
          session: response.data.session,
        };
      }

      // After challenge response success, process the authentication payload
      const pages = Object.entries(metadata.pages);
      const authorizedPages = response.data.table_access
        .map(
          (formTableName: string) =>
            pages.find(([_, page]) => page.table_name === formTableName)?.[0],
        )
        .filter((pageId: string | undefined) => pageId !== undefined);

      window.localStorage.setItem(role_key, response.data.role);
      window.localStorage.setItem(
        admin_flag_key,
        JSON.stringify(Boolean(response.data.is_admin)),
      );
      window.localStorage.setItem(token_key, response.data.access_token);
      if (response.data.refresh_token) {
        window.localStorage.setItem(
          refresh_token_key,
          response.data.refresh_token,
        );
      }
      window.localStorage.setItem(church_id_key, response.data.church_id);
      window.localStorage.setItem(username_key, username);
      window.localStorage.setItem(
        authorized_pages_key,
        JSON.stringify(authorizedPages),
      );
      // Store raw table_access for permission checking during data fetching
      window.localStorage.setItem(
        table_access_key,
        JSON.stringify(response.data.table_access || []),
      );
      set({ loading: false });
      return {
        authenticated: true,
        isRoleAdmin: Boolean(response.data.is_admin),
      };
    } catch (error: any) {
      set({
        loading: false,
        modal: errorModal(error),
      });
    }

    return { authenticated: false };
  },
  register: async (
    email: string,
    username: string,
    password: string,
    phoneNumber: string,
    firstName: string,
    lastName: string,
    ministryJustification: string,
    mailingAddress: string,
  ) => {
    set({ loading: true });
    try {
      await axios({
        method: "post",
        url: metadata.endpoints.register,
        data: {
          table_name: "user_registrations",
          action: "submit_registration",
          email,
          username,
          password,
          phoneNumber,
          first_name: firstName,
          last_name: lastName,
          ministry_justification: ministryJustification,
          mailing_address: mailingAddress,
        },
      });

      set({ loading: false });
      return true;
    } catch (error: any) {
      set({
        loading: false,
        modal: errorModal(error),
      });
    }

    return false;
  },
  changePassword: async (
    username: string,
    oldPassword: string,
    newPassword: string,
  ) => {
    set({ loading: true });
    try {
      const response = await axios({
        method: "post",
        url: metadata.endpoints.changePassword,
        headers: {
          "X-Username": username,
        },
        data: {
          username,
          old_password: oldPassword,
          new_password: newPassword,
        },
      });

      // Backend returns { status: 'password_changed' } but no new token.
      // Do not overwrite local storage with undefined.
      // The current session remains valid.
      set({
        loading: false,
        modal: {
          isOpen: true,
          title: t("success.title") || "Success",
          content:
            t("success.password_changed") || "Password changed successfully.",
          closeText: t("errors.ok") || "OK",
        },
      });
      return true;
    } catch (error: any) {
      set({
        loading: false,
        modal: errorModal(error),
      });
    }

    return false;
  },
  forgotPassword: async (username: string, email: string) => {
    set({ loading: true });
    try {
      await axios({
        method: "post",
        url: (metadata.endpoints as any).forgotPassword,
        data: { username, email },
      });
      set({ loading: false });
      return true;
    } catch (error: any) {
      set({
        loading: false,
        modal: errorModal(error),
      });
    }
    return false;
  },
  confirmForgotPassword: async (
    username: string,
    confirmationCode: string,
    newPassword: string,
  ) => {
    set({ loading: true });
    try {
      await axios({
        method: "post",
        url: (metadata.endpoints as any).confirmForgotPassword,
        data: {
          username,
          confirmation_code: confirmationCode,
          new_password: newPassword,
        },
      });
      set({ loading: false });
      return true;
    } catch (error: any) {
      set({
        loading: false,
        modal: errorModal(error),
      });
    }
    return false;
  },
  showModal: (
    title: string,
    content: string | React.ReactNode,
    closeText?: string,
    actionButton?: React.ReactNode,
    closeAction?: string,
  ) => {
    set((_) => ({
      modal: {
        isOpen: true,
        title: title,
        content: content,
        closeText: closeText || "OK",
        actionButton: actionButton,
        closeAction: closeAction,
      },
    }));
  },
  closeModal: () => {
    set((_) => ({
      modal: {
        isOpen: false,
        title: undefined,
        content: undefined,
        action: undefined,
        actionButton: undefined,
      },
    }));
  },
  updateOptionsLocally: async (localDB: LocalDB) => {
    set({ loading: true });

    // Get user's accessible tables from localStorage
    const tableAccessString = window.localStorage.getItem(table_access_key);
    const tableAccess: string[] = tableAccessString
      ? JSON.parse(tableAccessString)
      : [];

    // Helper function to check if user has access to a table
    const hasTableAccess = (tableName: string): boolean => {
      // Check both exact match and case-insensitive variations
      return tableAccess.some(
        (t) => t.toLowerCase() === tableName.toLowerCase(),
      );
    };

    // Helper function to safely fetch options for a table
    const fetchTableOptions = async (
      tableName: string,
    ): Promise<any | null> => {
      try {
        const response = await axios({
          method: "get",
          url: metadata.endpoints.getOptions,
          params: { table: tableName },
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem(token_key)}`,
            "X-Username": window.localStorage.getItem(username_key),
          },
        });
        return response.status === 200 ? response.data : null;
      } catch (error: any) {
        // Silently ignore 403 errors - user simply doesn't have access
        if (error.response?.status !== 403) {
          console.warn(
            `Failed to load options for ${tableName}:`,
            error.message,
          );
        }
        return null;
      }
    };

    try {
      let groups: any = {};

      // Fetch the main grouped view - this view handles its own authorization
      // based on user's access to the underlying tables
      const mainResponse = await fetchTableOptions(
        "v_grouped_church_and_baptism_and_worship",
      );
      if (mainResponse?.groups) {
        groups = { ...mainResponse.groups };
      }

      // Fetch additional dimension tables only if user has access
      // dim_ministry options
      if (hasTableAccess("dim_ministry")) {
        const ministryData = await fetchTableOptions("dim_ministry");
        if (ministryData?.records) {
          groups["ministry_id"] = ministryData.records.map((m: any) => ({
            label: `${m.ministry_id} - ${m.ministry_name}`,
            value: m.ministry_id,
          }));
        }
      }

      // dim_discipleship_course options
      if (hasTableAccess("dim_discipleship_course")) {
        const courseData = await fetchTableOptions("dim_discipleship_course");
        if (courseData?.records) {
          groups["course_id"] = courseData.records.map((c: any) => ({
            label: `${c.course_id} - ${c.course_name}`,
            value: c.course_id,
          }));
        }
      }

      // dim_mission_project options
      if (hasTableAccess("dim_mission_project")) {
        const projectData = await fetchTableOptions("dim_mission_project");
        if (projectData?.records) {
          groups["project_id"] = projectData.records.map((p: any) => ({
            label: `${p.project_id} - ${p.project_name}`,
            value: p.project_id,
          }));
        }
      }

      // dim_ministry_role options
      if (hasTableAccess("dim_ministry_role")) {
        const roleData = await fetchTableOptions("dim_ministry_role");
        if (roleData?.records) {
          groups["ministry_role_id"] = roleData.records.map((r: any) => ({
            label: `${r.ministry_role_id} - ${r.role_name}`,
            value: r.ministry_role_id,
          }));
        }
      }

      // dim_mission_role options
      if (hasTableAccess("dim_mission_role")) {
        const missionRoleData = await fetchTableOptions("dim_mission_role");
        if (missionRoleData?.records) {
          groups["mission_role_id"] = missionRoleData.records.map((r: any) => ({
            label: `${r.mission_role_id} - ${r.role_name}`,
            value: r.mission_role_id,
          }));
        }
      }

      const optionKey = `${localDB.name}_options`;
      localStorage.removeItem(optionKey);
      localStorage.setItem(optionKey, JSON.stringify(groups));
      set({ loading: false });
    } catch (error: any) {
      set({
        loading: false,
        modal: errorModal(error),
      });
    }
  },
  getOptionsLocally: (localDB: LocalDB, key: string) => {
    const json = localStorage.getItem(`${localDB.name}_options`);

    if (json === null) return [];
    const obj = JSON.parse(json);
    return obj[key] || [];
  },
  getAllData: async (pageId: string) => {
    set({ loading: true });

    try {
      const response = await axios({
        method: "get",
        url: (metadata.pages as any)[pageId].endpoints.getAllData,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem(token_key)}`,
          "X-Username": window.localStorage.getItem(username_key),
        },
      });

      if (response.status === 200) {
        set({ loading: false });
        return response.data.records || response.data.groups || [];
      }
    } catch (error: any) {
      set({
        loading: false,
        modal: errorModal(error),
      });
    }
  },
  getDataById: async (
    pageId: string,
    id: string,
    primaryKey: string = "id",
  ) => {
    set({ loading: true });

    try {
      // Since backend doesn't support fetching by ID directly, we fetch the list and filter.
      // We use the getAllData endpoint.
      const response = await axios({
        method: "get",
        url: (metadata.pages as any)[pageId].endpoints.getAllData,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem(token_key)}`,
          "X-Username": window.localStorage.getItem(username_key),
        },
      });

      if (response.status === 200) {
        set({ loading: false });
        // The response body structure from TableViewFunction is { records: [...] } or { groups: [...] }
        const records = response.data.records || response.data.groups || [];
        // Find the record matching the ID
        const record = records.find(
          (r: any) => String(r[primaryKey]) === String(id),
        );
        return record;
      }
    } catch (error: any) {
      set({
        loading: false,
        modal: errorModal(error),
      });
    }
  },
  saveData: async (pageId: string, data: any, files: any, id?: string) => {
    set({ loading: true });

    try {
      const method = "post";

      let url = metadata.endpoints.submitForm;
      if (id)
        url = (metadata.pages as any)[pageId].endpoints.updateData.replace(
          "{id}",
          id,
        );

      if ((pageId === "site_users" || pageId === "user_registrations") && id) {
        url = (metadata.endpoints as any).submitForm;
      }

      const submissionData = {
        table_name: (metadata.pages as any)[pageId].table_name,
        church_id: window.localStorage.getItem(church_id_key),
        ...data,
      };

      // Special handling for specific pages/actions
      if (pageId === "site_users") {
        if (id) {
          // Updating site_users roles
          submissionData.table_name = "site_users";
          submissionData.action = "patch_user_roles";
          submissionData.email = data.email; // Username is email in this system
          // Ensure roles is array
          if (!Array.isArray(submissionData.roles)) {
            submissionData.roles = [submissionData.roles];
          }
        } else {
          // Creating new user from "Active Users" page -> map to registration flow
          submissionData.table_name = "user_registrations";
          submissionData.action = "submit_registration";

          // Map fields that might be missing if not filled (though required on form)
          submissionData.ministry_justification = data.ministry_justification;
          submissionData.mailing_address = data.mailing_address;
          submissionData.is_admin =
            data.is_admin === "1" ||
            data.is_admin === 1 ||
            data.is_admin === true
              ? 1
              : 0;

          if (data.roles && !Array.isArray(data.roles)) {
            submissionData.roles = [data.roles];
          }
        }
      } else if (pageId === "user_registrations") {
        if (id) {
          // Approving/Rejecting registration
          submissionData.table_name = "user_registrations";
          submissionData.registration_id = Number(id);

          if (data.status === "approved" || data.status === "Approved") {
            submissionData.action = "approve_registration";
            // Ensure roles is array
            if (!Array.isArray(submissionData.roles)) {
              submissionData.roles = [submissionData.roles];
            }
          } else if (data.status === "rejected" || data.status === "Rejected") {
            submissionData.action = "reject_registration";
            submissionData.reason =
              data.rejection_reason || "Rejected by admin";
          }
        } else {
          // Creating a NEW registration request (Admin manually adding a user)
          submissionData.table_name = "user_registrations";
          submissionData.action = "submit_registration";
          submissionData.is_admin =
            data.is_admin === "1" ||
            data.is_admin === 1 ||
            data.is_admin === true
              ? 1
              : 0;
          // Ensure roles is array
          if (data.roles && !Array.isArray(data.roles)) {
            submissionData.roles = [data.roles];
          }
        }
      } else if (pageId === "dim_community") {
        // TODO: Remove this backend validation bypass
        submissionData.personal_id = "TBD";
      }

      const hasFiles = files && Object.keys(files).length > 0;

      let requestData: any;
      let headers: any = {
        Authorization: `Bearer ${window.localStorage.getItem(token_key)}`,
        "X-Username": window.localStorage.getItem(username_key),
      };

      if (hasFiles) {
        // Use FormData for file uploads
        const formData = new FormData();
        for (const key in submissionData) {
          formData.append(key, submissionData[key]);
        }
        for (const key in files) {
          for (const file of files[key]) {
            formData.append(key, file);
          }
        }
        requestData = formData;
        // No Content-Type needed - browser sets it automatically
      } else {
        // Use plain JSON for regular data
        requestData = submissionData;
        headers["Content-Type"] = "application/json";
      }

      // Invoke api
      const response = await axios({
        method: method,
        url: url,
        data: requestData,
        headers: headers,
      });

      if (response.status === 201 || response.status === 200) {
        set({ loading: false });
        return true;
      }
    } catch (error: any) {
      set({
        loading: false,
        modal: errorModal(error),
      });
    }
    return false;
  },
}));

const errorModal = (error: any) => {
  console.log(error);
  let message = JSON.stringify(error.message);
  let closeAction = undefined;

  if (error.response === undefined) {
    message = t("errors.services_down");
  } else {
    const { status, data } = error.response;

    if (status === 401) {
      message = data === "" ? t("errors.session_expired") : data.message;
      closeAction = "SignIn";
    } else if (status === 406) {
      message = data.message;
      closeAction = "ChangePassword";
    } else {
      message = data.message;
    }
  }

  return {
    isOpen: true,
    content: message,
    closeText: t("errors.ok"),
    closeAction,
  };
};
